
import ImageViewerDialog from 'smartbarcode-web-core/src/components/ImageViewerDialog.vue'
import IconEdit from '@/components/svg/IconEdit.vue'
import IconImage from '@/components/svg/IconImage.vue'
import IconListFile from 'smartbarcode-web-core/src/components/svg/IconListFile.vue'
import IconMaskedLock from '@/components/svg/IconMaskedLock.vue'
import IconSignature from '@/components/svg/IconSignature.vue'
import { getFilePathFromURL, toSnakeCase } from '@/utils/helpers'
import { localFileUrl } from '@/utils/localFileUrls'
import { isEmpty } from 'smartbarcode-web-core/src/utils/typeChecker'
import {
  ICustomFieldData,
  IDynamicCustomFieldData,
  IFile,
  ILabelDisplay,
  IListObjectItem,
  IListStringItem,
} from 'smartbarcode-web-core/src/utils/types/index'
import cloneDeep from 'lodash/cloneDeep'
import { Options, Vue } from 'vue-class-component'
import { Prop } from 'vue-property-decorator'
import { DATE_TIME, MASKED_ANNOTATION } from 'smartbarcode-web-core/src/utils/constants'
import { objectToArray, arrayToObject } from 'smartbarcode-web-core/src/utils/helpers'
import { Linkify } from '@/utils/directives'
import { ECustomFieldType } from 'smartbarcode-web-core/src/utils/enums'
import AdjustmentDataDialog from '@/components/dialog/AdjustmentDataDialog.vue'
import ListObjectView from 'smartbarcode-web-core/src/lib-components/ListObjectView.vue'
import { ICustomFieldShowData } from '@/store/type'
import { format } from 'date-fns'
import { ENTRY_VIEW } from '@/utils/constants'

export interface IFileInfo {
  files: {
    url: string
    name: string
  }[]
  label: string
}

@Options({
  directives: {
    linkify: Linkify,
  },
  components: {
    IconEdit,
    IconImage,
    IconMaskedLock,
    IconSignature,
    IconListFile,
    ImageViewerDialog,
    AdjustmentDataDialog,
    ListObjectView,
  },
  name: 'DetailAddressBox',
  methods: { toSnakeCase },
  emits: ['update:onRefreshReferenceFieldClick'],
})
export default class DetailAddressBox extends Vue {
  @Prop({ type: String }) readonly title!: string
  @Prop({ type: Object }) readonly eSigns!: ICustomFieldData[]
  @Prop({ type: Boolean }) readonly isMaskedImage!: boolean
  @Prop({ type: Boolean }) readonly isMaskedSignature!: boolean
  @Prop({ type: String }) readonly view?: string
  @Prop({ type: Object }) readonly data!: Record<string, ILabelDisplay>

  @Prop({ type: Object }) readonly fileLinks!: Record<string, ICustomFieldData>
  @Prop({ type: Object }) readonly fileImages!: Record<string, IDynamicCustomFieldData>
  @Prop({ type: Boolean }) readonly editable?: boolean
  @Prop({ type: Boolean }) readonly hasReferenceFields?: boolean
  localeList = Object.freeze(['ja', 'en', 'en-us', 'zh-tw', 'zh-cn'])

  imageDialogVisible = false
  signatureDialogVisible = false
  eSignCarouselIndex = 0
  imageCarouselIndex = 0
  image = {}
  isShowAdjustmentField = false
  adjustmentField = {} as ICustomFieldShowData
  listStringModalVisible = false
  listStringData: IListStringItem[] = []
  currentFieldLabel = ''

  get imageObj() {
    if (this.fileImages) {
      // FilterData use later
      const filterData = Object.values(this.fileImages).reduce((acc, curr) => {
        if (curr.files) {
          curr.files = curr.files.map((val) => {
            return { ...val, label: curr.label }
          })
          return { ...acc, [curr.label || '']: curr.files }
        }
        return acc
      }, {})
      return filterData || {}
    }
    return {}
  }

  get imageCarouselHeaderLabel() {
    return this.imageUrlsArr?.[this.imageCarouselIndex]?.label || ''
  }

  get eSignCarouselHeaderLabel() {
    return this.eSignsArr[this.eSignCarouselIndex]?.label || ''
  }

  get eSignsArr() {
    return this.eSigns?.filter((e) => !!e?.eSign) || []
  }

  get imageUrlsArr() {
    if (this.fileImages) {
      let data: IFile[] = []
      // FilterData use later
      Object.values(this.fileImages).map((curr) => {
        if (curr.files) {
          data = [...data, ...curr?.files]
        }
      }, {})
      return data
    }

    return []
  }

  get isQuickTrackingMode() {
    return this.$store.getters.isQuickTrackingModeEnabled
  }

  listHiddenKeysQuickTracking = ['externalId', 'center', 'maker']

  get dataList() {
    if (this.data) {
      const data = objectToArray(cloneDeep(this.data))
      return arrayToObject(
        data.filter((data) => {
          if (this.isQuickTrackingMode && this.listHiddenKeysQuickTracking.includes(data.key)) {
            return false
          }
          return !isEmpty(data.value.value)
        })
      )
    }
  }

  get isCardLayout() {
    return (
      !this.isQuickTrackingMode &&
      (this.view === ENTRY_VIEW.ORIGIN || this.view === ENTRY_VIEW.DESTINATION || this.view === ENTRY_VIEW.PACKAGE_INFO)
    )
  }

  get fileList(): { [key: string]: IFileInfo } {
    const fileLinks = this.fileLinks || {}
    const files = Object.keys(fileLinks).reduce((acc, currKey) => {
      if (!isEmpty(fileLinks?.[currKey].files)) {
        return { ...acc, [currKey]: this.fileLinks[currKey] }
      }
      if (fileLinks && !isEmpty(fileLinks?.[currKey]?.reference?.file)) {
        return { ...acc, [currKey]: { ...this.fileLinks[currKey], files: [this.fileLinks[currKey]?.reference?.file] } }
      }
      return acc
    }, {})
    return files
  }

  get isFilesExist() {
    return !isEmpty(this.fileList)
  }

  get MASKED_ANNOTATION() {
    return MASKED_ANNOTATION
  }

  get isAuth() {
    return this.$store.getters.isAuth
  }

  get project() {
    return this.$store.state.project.details
  }

  isAdjustmentField(fieldType?: ECustomFieldType) {
    return fieldType === ECustomFieldType.ADJUSTMENT
  }

  isListStringField(fieldType?: ECustomFieldType) {
    return fieldType === ECustomFieldType.LIST_STRING
  }

  isListObjectField(fieldType?: ECustomFieldType) {
    return fieldType === ECustomFieldType.LIST_OBJECT
  }

  onImageCarouselChange(idx: number) {
    this.imageCarouselIndex = idx
  }

  onESignCarouselChange(idx: number) {
    this.eSignCarouselIndex = idx
  }

  onShowAdjustment(cfKey: string, item: ILabelDisplay) {
    this.isShowAdjustmentField = true
    this.adjustmentField = {
      cfKey,
      label: item.label,
      value: item.value,
    }
  }

  // A just-submitted item carries only upload paths; the signed url arrives on
  // the next read, so show the local copy until then.
  withLocalFileUrls(items: IListObjectItem[]): IListObjectItem[] {
    return (items ?? []).map((item) => {
      const customFields = { ...(item?.customFields ?? {}) }
      Object.keys(customFields).forEach((code) => {
        const data = customFields[code]
        if (data?.fieldType !== ECustomFieldType.FILES) return
        customFields[code] = {
          ...data,
          files: (data.files ?? []).map((file) => ({ ...file, url: file.url || localFileUrl(file.path) })),
        }
      })
      return { ...item, customFields }
    })
  }

  onPreview(url: string) {
    window.open(url, '_blank')
  }

  onDisplayActivateImage() {
    if (this.isMaskedImage) return
    this.imageDialogVisible = true
  }

  onDisplaySignature() {
    if (this.isMaskedSignature) return
    this.signatureDialogVisible = true
  }

  getFileNameFromLink(linkFile: { url: string }) {
    return getFilePathFromURL(linkFile.url || '')
  }

  isTranslateAvailable(key: string) {
    let isFound = false
    if (!this.localeList) return
    this.localeList.forEach((val) => {
      if (this.$te(key, val)) {
        isFound = true
      }
    })
    return isFound
  }

  getDisplayValue(value: string | number | IListStringItem[], fieldType?: ECustomFieldType) {
    if (fieldType === ECustomFieldType.DATE_TIME_DATA && !Array.isArray(value)) {
      return format(new Date(value), DATE_TIME.WITH_SECOND)
    }
    return value
  }

  onClickEdit() {
    this.$router.push({ name: 'edit', params: { view: this.view } })
  }

  onClickRefreshReferenceField() {
    this.$emit('update:onRefreshReferenceFieldClick')
  }

  showListStringModal(data: IListStringItem[], fieldLabel: string) {
    if (Array.isArray(data)) {
      this.listStringData = data.map((item) => {
        if (typeof item === 'string') {
          return { value: item, createdDateTime: '' }
        }
        return item
      })
    } else {
      this.listStringData = []
    }
    this.currentFieldLabel = fieldLabel
    this.listStringModalVisible = true
  }

  formatDateTime(dateTime: string) {
    if (!dateTime) return ''
    try {
      return format(new Date(dateTime), DATE_TIME.WITH_SECOND)
    } catch (error) {
      return dateTime
    }
  }
}
